import {
  Input,
  cn,
  buildRelatedSkillSuggestions,
  resolveSkillNameFromSuggestions,
} from '@yeshuman/sdk'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'

import { useSkills } from '../../hooks/use-skills'
import {
  SkillSuggestionListbox,
  SkillSuggestionOption,
} from './skill-suggestion-listbox'
import { useComboboxPanelClose } from './use-combobox-panel-close'

const MIN_CHARS_FOR_SUGGESTIONS = 2
const MAX_SUGGESTIONS = 8

export function SkillNameComposer({
  id: idProp,
  value,
  onValueChange,
  excludeNames,
  disabled,
  placeholder = 'Add skill…',
  autoFocus,
  onCommit,
  onCancel,
  className,
  enableSuggestions = true,
  commitOnBlur = false,
  suggestionItems,
  suggestionsLoading = false,
}: {
  id?: string
  value: string
  onValueChange: (next: string) => void
  excludeNames: string[]
  disabled?: boolean
  placeholder?: string
  autoFocus?: boolean
  onCommit: (skillName: string) => void
  onCancel?: () => void
  className?: string
  enableSuggestions?: boolean
  commitOnBlur?: boolean
  suggestionItems?: { name: string }[]
  suggestionsLoading?: boolean
}) {
  const generatedId = useId()
  const id = idProp ?? generatedId
  const panelId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const skipBlurCommitRef = useRef(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const { clearBlurTimer, closePanelSoon } = useComboboxPanelClose(() => setPanelOpen(false))

  const { skills: catalogSkills, isLoading: catalogLoading } = useSkills()
  const isLoading = suggestionItems ? suggestionsLoading : suggestionsLoading || catalogLoading

  const excludedLower = useMemo(
    () => new Set(excludeNames.map((name) => name.trim().toLowerCase()).filter(Boolean)),
    [excludeNames]
  )

  const suggestionPool = useMemo(() => {
    if (suggestionItems) return suggestionItems
    return buildRelatedSkillSuggestions(catalogSkills, undefined)
  }, [catalogSkills, suggestionItems])

  const suggestions = useMemo(() => {
    if (!enableSuggestions) return []
    const query = value.trim().toLowerCase()
    if (query.length < MIN_CHARS_FOR_SUGGESTIONS) return []

    return suggestionPool
      .filter((item) => {
        const key = item.name.trim().toLowerCase()
        if (!key || excludedLower.has(key)) return false
        return item.name.toLowerCase().includes(query)
      })
      .slice(0, MAX_SUGGESTIONS)
  }, [enableSuggestions, excludedLower, suggestionPool, value])

  useEffect(() => {
    if (!autoFocus) return
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => window.clearTimeout(timer)
  }, [autoFocus])

  const commitValue = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed) return

    if (!enableSuggestions) {
      onCommit(trimmed)
      return
    }

    onCommit(resolveSkillNameFromSuggestions(trimmed, suggestions))
  }, [enableSuggestions, onCommit, suggestions, value])

  const pickSuggestion = (name: string) => {
    clearBlurTimer()
    onCommit(name)
    setPanelOpen(false)
  }

  const showPanel =
    enableSuggestions &&
    panelOpen &&
    !disabled &&
    value.trim().length >= MIN_CHARS_FOR_SUGGESTIONS &&
    (isLoading || suggestions.length > 0)

  const handleBlur = () => {
    if (commitOnBlur && !skipBlurCommitRef.current && !disabled) {
      const trimmed = value.trim()
      if (!trimmed) {
        onCancel?.()
      } else {
        commitValue()
      }
    }
    skipBlurCommitRef.current = false
    closePanelSoon()
  }

  return (
    <div className={cn('relative min-w-0 flex-1', className)}>
      <Input
        ref={inputRef}
        id={id}
        role={enableSuggestions ? 'combobox' : undefined}
        aria-expanded={enableSuggestions ? showPanel : undefined}
        aria-controls={showPanel ? panelId : undefined}
        aria-autocomplete={enableSuggestions ? 'list' : undefined}
        value={value}
        onChange={(event) => {
          onValueChange(event.target.value)
          if (enableSuggestions) setPanelOpen(true)
        }}
        onFocus={() => {
          if (enableSuggestions) setPanelOpen(true)
        }}
        onBlur={handleBlur}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            skipBlurCommitRef.current = true
            commitValue()
            setPanelOpen(false)
          }
          if (event.key === 'Escape') {
            event.preventDefault()
            skipBlurCommitRef.current = true
            setPanelOpen(false)
            onCancel?.()
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className="h-8 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
      />
      {showPanel ? (
        <SkillSuggestionListbox id={panelId} maxHeightClassName="max-h-40" isLoading={isLoading}>
          {suggestions.map((item) => (
            <SkillSuggestionOption key={item.name} name={item.name} onPick={pickSuggestion} />
          ))}
        </SkillSuggestionListbox>
      ) : null}
    </div>
  )
}
